import React, { memo, useEffect, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import withClientOnlyRender from "@/hocs/withClientOnlyRender";
import {
  useLanguage,
  useRouteSlug,
  useCurrency,
  useIsLoggedIn,
  useCountryId
} from "@/hooks";

import ProductService from "@/services/productService";

import { fetchLoyaltyDetails } from "@/redux/actions/myAccount.action";

import { getLoyaltyBody } from "@/util/loyalty-points";
import {
  selectAccessToken,
  selectIsJoinedLoyaltyProgram,
  selectStaticAssets
} from "@/util/selectors";
import { useAppSelector } from "@/redux/store";
import { selectProductDetails } from "../selectors";

import "./style.scss";

const LoyaltyPointsInfo = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [loyaltyPoints, setLoyaltyPoints] = useState<number>();

  const accessToken = useSelector(selectAccessToken);
  const isJoinedToLoyaltyProgram = useSelector(selectIsJoinedLoyaltyProgram);
  const product = useSelector(selectProductDetails);

  const currency = useCurrency();
  const countryId = useCountryId();
  const language = useLanguage();
  const routeSlug = useRouteSlug();
  const isLoggedIn = useIsLoggedIn();
  const staticAssets = useAppSelector(selectStaticAssets);

  const dispatch = useDispatch();
  const { t } = useTranslation("productDetails");

  useEffect(() => {
    const loadPoints = async () => {
      const isJoined =
        isLoggedIn &&
        (isJoinedToLoyaltyProgram ||
          ((await dispatch(fetchLoyaltyDetails(language))) as any)
            ?.loyaltyDetails);

      if (isJoined) {
        const { orderItems } = getLoyaltyBody({ cart: [product] });
        const points = await ProductService.getLoyaltyPointsByProducts(
          orderItems
        );
        setLoyaltyPoints(points);
      }

      setIsLoaded(true);
    };

    loadPoints();
  }, [isLoggedIn, product, currency]);

  if (!isLoaded) return null;

  return (
    <p className="earnPoints">
      {accessToken && isJoinedToLoyaltyProgram && isLoggedIn && (
        <Trans i18nKey="youWillEarnUpTo" ns="productDetails">
          {/* @ts-ignore */}
          You will earn up to <span>{{ loyaltyPoints }}</span> loyalty points
          when you complete this purchase
        </Trans>
      )}

      {!isLoggedIn && (
        <>
          <Link
            className="loyalty-join"
            to={{
              pathname: `/${routeSlug}/login/`,
              search: `?pdpRedirectionUrl=${product.url}`
            }}
          >
            {t("signIn")}
          </Link>

          {t("signInAndJoin")}
        </>
      )}

      {accessToken && isLoggedIn && !isJoinedToLoyaltyProgram && (
        <>
          <Link
            className="loyalty-join"
            to={`/${routeSlug}/my-account/loyalty-program/`}
          >
            {t("join")}
          </Link>
          {t("toEarnPoints")}
        </>
      )}

      <Link
        className="learn-more-text"
        to={`/${routeSlug}${staticAssets?.loyaltyProgramSlug}`}
      >
        {t("learnMore")}
      </Link>
    </p>
  );
};

export default memo(withClientOnlyRender(LoyaltyPointsInfo));
